import React, { useEffect, useState } from "react";
import { Button, Drawer, Form, Input, message, Select, Spin } from "antd";
import { PlusOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import {
  AddCustomers,
  FetchCustomerData_api,
  FetchCustomerList_api,
  UpdateCustomers,
} from "../../Api";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { useAuthChecker } from "../../Context";
import { Pagination } from "antd";
import { CustomerView } from "./common/CustomerView";
import { ManageCustomer } from "./common/ManageCustomer";

const { Option } = Select;

export const ManageCustomerPage = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<any>(null);
  const [customerForm] = Form.useForm();
  const { user } = useAuthChecker();

  // View Drawer State
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState<any>(null);

  const [searchKeywordForCustomerSearch, setSearchKeywordForCustomerSearch] =
    useState("");
  const [debouncedQueryForCustomerSearch] = useDebounce(
    searchKeywordForCustomerSearch,
    500,
  );
  const [customerData, setCustomerData] = useState<any>([]);
  const [initialCustomerData, setInitialCustomerData] = useState<any>([]);
  const [addedCustomerData, setAddedCustomeData] = useState<any>([]);

  const [customersList, setCustomersList] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);

  // Pagination State for Table
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // New Filters
  const [filterQuery, setFilterQuery] = useState("");
  const [isPaidCompany, setIsPaidCompany] = useState<string | undefined>(
    undefined,
  );
  const [dueFilter, setDueFilter] = useState<string | undefined>(undefined);

  // Dropdown Search
  const { isFetching: isFetchingCustomers } = useQuery<any>({
    queryKey: ["initialCustomerListDropdown"],
    queryFn: async () => {
      const response: any = await FetchCustomerData_api(null, 1, 10);
      setInitialCustomerData(response.customerData || []);
      setCustomerData(response.customerData || []);
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const handleSearch = async (keyword: string) => {
    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) {
      setCustomerData(initialCustomerData);
      return;
    }

    const localMatches = initialCustomerData.filter(
      (c: any) =>
        c.name.toLowerCase().includes(trimmed) ||
        c.mobile.toLowerCase().includes(trimmed),
    );

    if (localMatches.length > 0) {
      setCustomerData(localMatches);
    }
  };

  // Table Data
  const { refetch: FetchCustomerList, isFetching: isFetchingCustomersList } =
    useQuery<any>({
      queryKey: [
        "searchCustomer",
        addedCustomerData?.id,
        page,
        pageSize,
        filterQuery,
        isPaidCompany,
        dueFilter,
      ],
      queryFn: async () => {
        const response: any = await FetchCustomerList_api(
          addedCustomerData?.id,
          page,
          pageSize,
          {
            search: filterQuery,
            is_paid_company: isPaidCompany,
            due_filter: dueFilter,
          },
        );
        setCustomersList(response.customerData);
        setTotalCustomers(response.total);
        return response;
      },
      enabled: true,
      refetchOnWindowFocus: false,
    });

  const handleAdd = () => {
    setEditingCustomer(null);
    setOpen(true);
  };

  const handleEdit = (record: any) => {
    setEditingCustomer(record);
    setOpen(true);
  };

  const handleView = (record: any) => {
    setViewingCustomer(record);
    setViewDrawerOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCustomer(null);
  };

  const handleFinish = async () => {
    setSubmitting(true);
    try {
      const values = customerForm.getFieldsValue();

      const payload =
        editingCustomer && editingCustomer.id
          ? {
              customer_id: editingCustomer.id,
              inquiries: values.inquiries,
              userId: user?.userId,
            }
          : { ...values, userId: user?.userId };

      const res =
        editingCustomer && editingCustomer?.id
          ? await UpdateCustomers(payload)
          : await AddCustomers(payload);

      res?.isDataAdded && customerForm?.resetFields();
      res?.msg && message.success(res.msg);
      handleClose();
    } catch (error) {
    } finally {
      setSubmitting(false);
      FetchCustomerList(); // Refresh list
    }
  };

  useEffect(() => {
    const trimmed = debouncedQueryForCustomerSearch.trim().toLowerCase();
    if (!trimmed) {
      setCustomerData(initialCustomerData);
      return;
    }

    const localMatches = initialCustomerData.filter(
      (c: any) =>
        c.name.toLowerCase().includes(trimmed) ||
        c.mobile.toLowerCase().includes(trimmed),
    );

    if (localMatches.length === 0) {
      const fetchFromBE = async () => {
        const response: any = await FetchCustomerData_api(trimmed, 1, 10);
        setCustomerData(response.customerData || []);
      };
      fetchFromBE();
    }
  }, [debouncedQueryForCustomerSearch]);

  useEffect(() => {
    FetchCustomerList();
  }, [
    addedCustomerData,
    page,
    pageSize,
    filterQuery,
    isPaidCompany,
    dueFilter,
  ]);

  useEffect(() => {
    setPage(1);
  }, [addedCustomerData]);

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Customer Management</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Customer
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Search Customer / Vehicle / Inq
          </p>
          <Input
            placeholder="Name, Mobile, Engine, Chassis, Inq..."
            prefix={<PlusOutlined className="rotate-45" />}
            value={filterQuery}
            onChange={(e) => {
              setFilterQuery(e.target.value);
              setPage(1);
            }}
            allowClear
          />
        </div>

        <div>
          <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Quick Select Customer
          </p>
          <Select
            showSearch
            loading={isFetchingCustomers}
            placeholder="Quick search customer..."
            optionFilterProp="children"
            className="w-full"
            onSearch={(value: string) => {
              setSearchKeywordForCustomerSearch(value);
              handleSearch(value);
            }}
            onChange={(value) => {
              const selectedCustomer = customerData?.find(
                (customer: any) => customer.id === value,
              );
              setAddedCustomeData(selectedCustomer);
            }}
            allowClear
            onClear={() => setAddedCustomeData(null)}
            value={addedCustomerData?.id}
          >
            {customerData?.map((data: any) => (
              <Option key={data.id} value={data.id}>
                {data.name} - {data.mobile}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Payment Status (Company)
          </p>
          <Select
            placeholder="All Status"
            className="w-full"
            allowClear
            value={isPaidCompany}
            onChange={(val) => {
              setIsPaidCompany(val);
              setPage(1);
            }}
          >
            <Option value="1">Paid (Company)</Option>
            <Option value="0">Unpaid (Company)</Option>
          </Select>
        </div>

        <div>
          <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Due Filter
          </p>
          <Select
            placeholder="All Customers"
            className="w-full"
            allowClear
            value={dueFilter}
            onChange={(val) => {
              setDueFilter(val);
              setPage(1);
            }}
          >
            <Option value="due">Due Customers</Option>
            <Option value="paid">Zero Due (Fully Paid)</Option>
          </Select>
        </div>
      </div>

      <div className="px-6 pb-6">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col h-[80vh]">
          <div className="flex-grow overflow-auto">
            {isFetchingCustomersList ? (
              <div className="flex items-center justify-center h-full">
                <Spin spinning={isFetchingCustomersList}></Spin>
              </div>
            ) : customersList?.length > 0 ? (
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-gray-100 text-gray-800 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="p-3 border-b text-left w-1/5">
                      Customer Details
                    </th>
                    <th className="p-3 border-b text-left">Inquiry #</th>
                    <th className="p-3 border-b text-left">Total (LKR)</th>
                    <th className="p-3 border-b text-left">
                      Price breakdown (LKR)
                    </th>
                    <th className="p-3 border-b text-left">Paid (LKR)</th>
                    <th className="p-3 border-b text-left">Due (LKR)</th>
                    <th className="p-3 border-b text-left">is Batapola</th>
                    <th className="p-3 border-b text-left">is Paid</th>
                    <th className="p-3 border-b text-center w-32">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {customersList?.map((customer: any) => {
                    const inquiries = customer.inquiries || [];
                    const rowSpan = inquiries.length || 1;

                    if (inquiries.length === 0) {
                      return (
                        <tr
                          key={`${customer.id}-empty`}
                          className="hover:bg-gray-50 border-b"
                        >
                          <td className="p-3 font-semibold align-top border-r">
                            {customer.name} - {customer.mobile}
                          </td>
                          <td
                            colSpan={6}
                            className="p-3 text-gray-400 text-center italic"
                          >
                            No inquiries found
                          </td>
                          <td className="p-3 border-l text-center align-top space-x-2">
                            <Button
                              size="small"
                              icon={<EyeOutlined />}
                              onClick={() => handleView(customer)}
                            />
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => handleEdit(customer)}
                            >
                              Edit
                            </Button>
                          </td>
                        </tr>
                      );
                    }

                    return inquiries.map((inq: any, index: number) => (
                      <tr
                        key={`${customer.id}-${index}`}
                        className="hover:bg-gray-50 border-b last:border-b-0"
                      >
                        {index === 0 && (
                          <td
                            rowSpan={rowSpan}
                            className="p-3 font-semibold align-top border-r bg-white"
                          >
                            {customer.name} - {customer.mobile}
                          </td>
                        )}

                        <td className="p-3 border-r">
                          <span
                            className={
                              inq.status?.toLowerCase() === "pending"
                                ? "text-red-600 font-medium"
                                : "text-green-600 font-medium"
                            }
                          >
                            <span className="text-sm font-bold">
                              {inq.inquiryNumber}
                            </span>{" "}
                            <span className="text-xs">({inq.status})</span>
                            <br />
                            <span className="text-gray-600 text-xs font-semibold">
                              {inq.vehicleType} - {inq.vehicleModel}
                            </span>
                            {inq.engine_number && (
                              <div className="mt-1 text-xs text-blue-600 bg-blue-50 p-1 rounded border border-blue-100 inline-block">
                                <span className="font-semibold">
                                  EnginID: {inq.engine_number}
                                </span>
                              </div>
                            )}
                          </span>
                        </td>

                        <td className="p-3 border-r">
                          {parseFloat(inq.total).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="p-3 border-r">
                          <div className="flex flex-col">
                            <div className="flex justify-between">
                              <span>Downpayment:</span>
                              <span>
                                {parseFloat(inq.downpayment).toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Loan Amount:</span>
                              <span>
                                {parseFloat(inq.loanamount).toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 border-r text-green-600">
                          {parseFloat(inq.paid).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td
                          className={`p-3 border-r ${
                            inq.due > 0 ? "text-red-600" : "text-green-600"
                          }`}
                        >
                          {parseFloat(inq.due).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="p-3 border-r">
                          {inq.isBatapola == 1 ? "Yes" : "No"}
                        </td>

                        <td
                          className={`p-3 border-r ${
                            inq.isPaidCompany != 0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {inq.isPaidCompany != 0 ? "Yes" : "No"} / (
                          {parseFloat(inq.company_paid_amount).toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                          )
                        </td>

                        {index == 0 && (
                          <td
                            rowSpan={rowSpan}
                            className="p-3 text-center align-top bg-white space-x-1"
                          >
                            <Button
                              size="small"
                              icon={<EyeOutlined />}
                              className="text-blue-600 border-blue-600 hover:text-blue-500 hover:border-blue-500"
                              onClick={() => handleView(customer)}
                            />
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => handleEdit(customer)}
                            />
                          </td>
                        )}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-gray-500 h-full">
                <p>No customer data available</p>
              </div>
            )}
          </div>
          {/* Pagination Footer */}
          <div className="p-4 border-t bg-white flex justify-end">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={totalCustomers}
              onChange={(p, ps) => {
                setPage(p);
                setPageSize(ps);
              }}
              showSizeChanger
              showTotal={(total) => `Total ${total} customers`}
            />
          </div>
        </div>
      </div>

      <Drawer
        title={editingCustomer ? "Edit Customer" : "Add Customer"}
        open={open}
        onClose={handleClose}
        width="75%"
        destroyOnClose
      >
        <ManageCustomer
          submitting={submitting}
          form={customerForm}
          handleFinish={handleFinish}
          initialValues={editingCustomer}
        />
      </Drawer>
      <CustomerView
        open={viewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
        customer={viewingCustomer}
      />
    </div>
  );
};
